<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { httpPost } from '@/lib/http'
import Cookies from 'js-cookie'
import AccountLogoutForm from '@/ui-kit/forms/AccountLogoutForm.vue'
import GameCard from '@/ui-kit/student/GameCard.vue'
import GameKaraoke from '@/games/GameKaraoke.vue'
import GameShadowing from '@/games/GameShadowing.vue'
import StudentBadBrowserNotification from '@/ui-kit/student/BadBrowserNotification.vue'
import StudentCrystalsNotificationCard from '@/ui-kit/student/CrystalsNotificationCard.vue'
import StudentGamePurchaseModal from '@/ui-kit/student/GamePurchaseModal.vue'
import StudentGamesHelpModal from '@/ui-kit/student/GamesHelpModal.vue'
import StudentPwaButton from '@/ui-kit/student/PwaButton.vue'
import StudentShopModal from '@/ui-kit/student/ShopModal.vue'
import StudentSmartBookCard from '@/ui-kit/student/SmartBookCard.vue'
import StudentTailPassed from '@/ui-kit/student/StudentTailPassed.vue'
import StudentV2CheckEmailModal from '@/ui-kit/student2/CheckEmailModal.vue'
import { currentUser } from '@/lib/currentUser'
import { AudioActivityTracker, CustomActivityTracker } from '@/helpers/trackers'
import { pikimoni } from '@/helpers/pikimoni'
import type { OpenModalPayload, PaymentModalExpose } from '~/ui-kit/components/PaymentModal/PaymentModal.vue'
import { useStaticAsset } from '@/composables/useStaticAsset'
import { useHoliday, useHolidayAssets } from '~/composables/useHoliday'

const asset = useStaticAsset()

// TypeScript interfaces
interface Tail {
  code: number
  junit_id: number
  name: string
  junit_name: string
  tail_type: string
}

interface Student {
  id: number
  name: string
  last_name: string
  patronymic: string
  victories_count: number
  has_pikimoni_binding: boolean
  gender: 'm' | 'f' | '-'
  tails: Tail[]
  just_passed_tails: Tail[]
}

interface Unit {
  id: number
  title: string
  unlocked: boolean
  lock_reason: string | null
}

interface CoursePart {
  id: number
  course_id: number
  title: string
  part: string
  units: Unit[]
  first?: boolean
}

interface Material {
  id: number
  name: string
  language: string
  url: string
}

interface GamePrice {
  unit_price: number
  course_part_price: number
}

interface Game {
  id: number
  friendly_name: string
  game_friendly_name?: string
  name: string
  cover_image: string
  url: string | null
  unlocked: boolean
  lock_reason: string | null
  demo: boolean
  price: GamePrice | null
  draft: boolean
}

interface SmartBook {
  star_count: number
}

interface PageContext {
  student: Student
  course_part: CoursePart
  smartbook_demo_url: string
}

interface UniqueMaterial {
  name: string
  id: number
  rus?: Material
  eng?: Material
  ukr?: Material
  tat?: Material
}

// Component refs interfaces
interface LogoutFormRef {
  open: (studentId: number) => void
}

interface ShopModalRef {
  $open: (studentId: number, coursePartId: number, tabIndex?: number) => void
}

interface CheckEmailModalRef {
  $prompt: (action: string) => Promise<boolean>
}

interface GameHelpModalRef {
  $open: (gameName: string) => void
}

interface MaterialsResponse {
  materials: Material[]
  games: Game[]
  smart_book: SmartBook | null
}

// Context
const ctx = usePageContext<PageContext>()

// Composables
const { notify } = useNotification()
const { isMobile } = useScreen()
const holiday = useHoliday()
const holidayAssets = useHolidayAssets()

// State from context
const student = ref<Student>(ctx.student)
const coursePart = ref<CoursePart>(ctx.course_part)
const smartbookDemoUrl = ref<string>(ctx.smartbook_demo_url)

// Local state
const activeUnitId = ref<number>(coursePart.value.units[0]?.id ?? 0)
const activeGame = ref<Game | null>(null)
const orderGame = ref<Game | null>(null)
const canPingGameActivity = ref(false)
const materialTrackers = ref<Record<number, { onAudioPlayed: () => void }>>({})
const gameTrackers = ref<Record<number, { startTracking: () => void; stopTracking: () => void }>>({})
const materials = ref<Material[]>([])
const games = ref<Game[]>([])
const animalPicCount = 16
const modalTail = ref<Tail | null>(null)
const smartBook = ref<SmartBook | null>(null)
const noCrystalsModal = ref(false)

// Template refs
const logoutForm = ref<LogoutFormRef | null>(null)
const shopModal = ref<ShopModalRef | null>(null)
const checkEmailModal = ref<CheckEmailModalRef | null>(null)
const paymentModal = ref<PaymentModalExpose | null>(null)
const gameHelpModal = ref<GameHelpModalRef | null>(null)

// Computed
const parentCabinetLink = computed(() => {
  const isParentDemo = currentUser.id === 0
  const isStudentDemo = student.value.id === 0

  if (isParentDemo === isStudentDemo) return '/parent/cabinet'
  return '/users/login?next=/parent/cabinet&tab=login'
})

const activeUnit = computed(() => {
  return coursePart.value.units.find(u => u.id === activeUnitId.value)
})

const activeUnitIndex = computed(() => {
  return coursePart.value.units.findIndex(u => u.id === activeUnitId.value)
})

const unitSelectItems = computed(() => {
  return coursePart.value.units.map(unit => ({
    text: unit.title,
    value: unit.id
  }))
})

const isActiveGameIntegrated = computed(() => {
  return activeGame.value && ['shadowing', 'karaoke', 'videoteacher', 'irregularverbs'].includes(activeGame.value.name)
})

const purchasedGames = computed(() => {
  return games.value.filter(g => g.unlocked && !g.demo)
})

const notPurchasedGames = computed(() => {
  return games.value.filter(g => !g.unlocked || g.demo)
})

const uniqueMaterials = computed<UniqueMaterial[]>(() => {
  if (!materials.value.length) return []

  const names = [...new Set(materials.value.map(m => m.name))]
  return names.map(name => {
    const sameMaterials = materials.value.filter(m => m.name === name)
    return {
      name,
      id: sameMaterials[0].id,
      rus: sameMaterials.find(m => m.language === 'rus'),
      eng: sameMaterials.find(m => m.language === 'eng'),
      ukr: sameMaterials.find(m => m.language === 'ukr'),
      tat: sameMaterials.find(m => m.language === 'tat')
    }
  })
})

const tailsStr = computed(() => {
  const num = student.value.tails.length
  const noun = pluralize(num, 'хвост', 'хвоста', 'хвостов')
  return `${num} ${noun}`
})

const randomAnimalNum = computed(() => {
  return Math.floor(Math.random() * animalPicCount) + 1
})

const unitsBackgroundImage = computed(() => {
  if (!holiday) return
  return `url(${holidayAssets.foregroundPic}), url(${holidayAssets.navPattern})`
})

// Methods
function pluralize(num: number, one: string, few: string, many: string): string {
  const mod10 = num % 10
  const mod100 = num % 100

  if (mod100 >= 11 && mod100 <= 19) return many
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}

function getMaterialFilePrefix(): string {
  const { title = '', part = '' } = coursePart.value
  return `${title}__${part}__${activeUnit.value?.title || ''}__`
}

function getCurrentUnit(): number {
  const { units } = coursePart.value
  if (units.length === 0) return 0

  const search = new URLSearchParams(window.location.search)
  const unitId = Number(search.get('unit_id')) || units[0].id
  return units.some(u => u.id === unitId) ? unitId : units[0].id
}

function openGame(game: Game): void {
  if (game.unlocked) {
    activeGame.value = game
    return
  }

  checkEmailModal.value?.$prompt(`приобрести приложение ${game.friendly_name}`).then(isValid => {
    if (!isValid) return
    orderGame.value = game
  })
}

function openShop(): void {
  checkEmailModal.value?.$prompt('открыть магазин ученика').then(isValid => {
    if (!isValid) return
    shopModal.value?.$open(student.value.id, coursePart.value.id, 1)
  })
}

async function goToUnit(unitId: number): Promise<void> {
  activeUnitId.value = unitId
  history.replaceState({ unitId: activeUnitId.value }, '', `?unit_id=${activeUnitId.value}`)

  const url = `/student/request_unit_materials/${student.value.id}/${activeUnitId.value}`
  const result = await httpPost<MaterialsResponse>(url, {}).catch(() => {
    notify({
      type: 'error',
      title: 'Не удалось получить материалы курса. Попробуйте обновить страницу.'
    })
    return null
  })

  if (!result) return

  materials.value = result.materials
  games.value = result.games
  smartBook.value = result.smart_book
}

function onUnitSelect(value: unknown): void {
  if (typeof value !== 'number') return
  void goToUnit(value)
}

async function loginToPikimoni(): Promise<void> {
  const win = window.open('about:blank', '_blank')
  try {
    const url = await pikimoni.getLoginUrl('student', student.value.id)

    // Small hack for Safari (does not allow async window.open)
    if (win) win.location.href = url
  } catch (err) {
    if (win) win.close()

    let title = 'Не удалось перейти ко входу в Pikimoni'
    if (typeof err === 'string') title = err
    if (err && typeof err === 'object' && 'message' in err) title = (err as Error).message
    notify({ type: 'error', title })
  }
}

function confirmPurchase(unitId?: number, isSmartBook = false): void {
  const query: OpenModalPayload['query'] = {}
  const game = orderGame.value ? { ...orderGame.value } : null

  if (!game) return

  let name = game.friendly_name

  if (isSmartBook) {
    query.course_id = coursePart.value.course_id
    query.student_ids = student.value.id
  } else {
    query.course_part_id = coursePart.value.id
    query.student_id = student.value.id
    query.game_id = game.id
  }

  let price = game.price?.course_part_price ?? 0
  if (unitId) {
    query.unit_id = unitId
    const unit = coursePart.value.units.find(e => e.id === unitId)

    name = `${game.friendly_name} - ${unit?.title ?? ''}`
    price = game.price?.unit_price ?? 0
  }

  orderGame.value = null
  paymentModal.value?.open({
    url: isSmartBook ? '/parent/request_purchase_smartbook' : '/student/request_purchase_game',
    order: {
      name: `${name} (доступ на 18 месяцев)`,
      price
    },
    query
  })
}

function onGamePlay(materialId: number): void {
  if (!gameTrackers.value[materialId]) {
    const isPlayingFunc = (): boolean => !!(activeGame.value && activeGame.value.id === materialId && !document.hidden)
    const trackFunc = (ms: number) => {
      const form = new FormData()
      form.append('csrfmiddlewaretoken', Cookies.get('csrftoken') ?? '')
      form.append('student_id', String(student.value.id))
      form.append('unit_id', String(activeUnitId.value))
      form.append('game_id', String(materialId))
      form.append('milliseconds', String(ms))
      navigator.sendBeacon('/student/track_game_activity', form)
    }
    gameTrackers.value[materialId] = new CustomActivityTracker(isPlayingFunc, trackFunc)
  }
  gameTrackers.value[materialId].startTracking()
}

function onGamePause(materialId: number): void {
  if (gameTrackers.value[materialId]) {
    gameTrackers.value[materialId].stopTracking()
  }
}

function onAudioPlay(materialId: number): void {
  if (!materialTrackers.value[materialId]) {
    const audioElement = document.querySelector<HTMLAudioElement>(`div[data-material-id="${materialId}"] > audio`)
    const studentId = student.value.id
    const courseId = coursePart.value.course_id

    const track = (milliseconds: number) => {
      const form = new FormData()
      form.append('csrfmiddlewaretoken', Cookies.get('csrftoken') ?? '')
      form.append('student_id', String(studentId))
      form.append('course_id', String(courseId))
      form.append('material_id', String(materialId))
      form.append('milliseconds', String(milliseconds))
      navigator.sendBeacon('/student/track_material_activity', form)
    }

    materialTrackers.value[materialId] = new AudioActivityTracker(audioElement!, track)
    materialTrackers.value[materialId].onAudioPlayed()
  }
}

function onGameMessage(event: MessageEvent): void {
  if (!event.data || typeof event.data !== 'string' || !event.data.startsWith('mg:')) {
    return
  }

  const [, key] = event.data.split(':')
  if (key === 'trackActivity') {
    canPingGameActivity.value = true
  }
}

async function onTailDismiss(): Promise<void> {
  // same as array.shift()
  student.value.just_passed_tails = student.value.just_passed_tails.slice(1)

  // unset the tail for a moment so the animation will restart
  modalTail.value = null
  await nextTick()
  modalTail.value = student.value.just_passed_tails[0] ?? null
}

function getAnimalNum(tail: Tail): number {
  const tailTypeNum = ['control_item', 'workbook_task', 'workbook_accept'].indexOf(tail.tail_type) + 1
  return ((tail.code + tail.junit_id + tailTypeNum) % animalPicCount) + 1
}

// Watchers
watch(activeGame, (newValue, oldValue) => {
  if (newValue === oldValue) return
  if (newValue) {
    onGamePlay(newValue.id)
  }
  if (oldValue) {
    onGamePause(oldValue.id)
  }
})

// Lifecycle
onMounted(() => {
  goToUnit(getCurrentUnit())
  window.addEventListener('message', onGameMessage, false)

  if (student.value.just_passed_tails?.length) {
    modalTail.value = student.value.just_passed_tails[0]
  }
})
</script>

<template>
  <div v-cloak id="account-child-study" class="account-frame">
    <v-notifications position="bottom right" />
    <AccountLogoutForm ref="logoutForm" />

    <div class="aside">
      <div class="student-logo">
        <v-logo :height="isMobile ? 40 : 64" />
      </div>

      <div class="aside__user user mt-4 mt-d-3" :data-id="student.id">
        <v-icon v-if="!isMobile" class="user__avatar" size="48">mdi-account</v-icon>

        <div>
          {{ student.last_name }} {{ student.name }} {{ student.patronymic }}
          <v-button v-if="student.id === 0" mode="small primary" class="mt-1" href="/users/login?student_login=true">
            Войти на сайт
          </v-button>
        </div>

        <div class="flex-spacer"></div>

        <v-menu-base v-if="student.id !== 0" pos-horizontal="right" no-mobile-modal>
          <template #activator="{ onclick }">
            <v-link @click="onclick">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-link>
          </template>

          <div class="pa-1">
            <v-menu-button mode="primary" :href="parentCabinetLink">
              <v-icon class="mr-2" size="20">mdi-human-male-child</v-icon>
              Перейти в Кабинет родителя
            </v-menu-button>

            <v-menu-button mode="error" @click="() => logoutForm?.open(student.id)">
              <v-icon class="mr-2" size="20">mdi-exit-run</v-icon>
              Выйти из аккаунта
            </v-menu-button>
          </div>
        </v-menu-base>
      </div>

      <v-button v-if="student.has_pikimoni_binding" mode="primary block" class="mt-2" @click="loginToPikimoni">
        Войти в Pikimoni
      </v-button>

      <div class="units" :style="{ backgroundImage: unitsBackgroundImage }">
        <div class="title" :data-id="coursePart.id">{{ coursePart.title }}</div>
        <div class="part">{{ coursePart.part }}</div>
        <div class="list">
          <div
            v-for="(unit, index) in coursePart.units"
            :key="unit.id"
            class="units__unit"
            :class="{
              done: index < activeUnitIndex,
              current: index === activeUnitIndex,
              locked: !unit.unlocked
            }"
            :data-id="unit.id"
            @click="goToUnit(unit.id)"
          >
            {{ unit.title }}
          </div>
        </div>
        <v-button mode="gray" :href="`/student/choose_course/${student.id}`">Другие курсы</v-button>
      </div>

      <div v-if="!smartBook" class="student-smartbook-ad mt-2">
        <img :src="asset('img/smartbook-logo.svg')" height="96" />

        <h6 class="text-body-bold mt-1">Умный Учебник<br />Я говорю по-английски / I Can Speak</h6>
        <ul class="mt-1">
          <li>— Электронное приложение</li>
          <li class="mt-half">— Обучающие мультфильмы</li>
          <li class="mt-half">— Интерактивные задания</li>
          <li class="mt-half">— Мотивационные кейсы</li>
        </ul>

        <v-button class="mt-2" mode="warn filled block" :href="smartbookDemoUrl" target="_blank">
          Попробовать
        </v-button>
        <v-button class="mt-1" mode="warn outlined block" href="/student/smartbook_info"> Подробнее </v-button>
      </div>

      <StudentPwaButton />
    </div>

    <StudentTailPassed
      :tail="modalTail"
      :animal-pic-count="animalPicCount"
      :student-id="student.id"
      @dismiss="onTailDismiss"
    />

    <StudentShopModal ref="shopModal" :cabinet-student-id="student.id" />
    <StudentV2CheckEmailModal ref="checkEmailModal" :demo="student.id === 0" />
    <v-payment-modal ref="paymentModal" :student-id="student.id" />

    <StudentBadBrowserNotification />

    <StudentGamesHelpModal ref="gameHelpModal" />

    <div v-if="activeUnitId" class="content" :data-child="student.id">
      <div class="header">
        <h1 class="title">{{ activeUnit?.title }}</h1>

        <v-button
          mode="primary small filled"
          class="mr-2 mr-d-4"
          :href="`/student/playlist/${student.id}/${coursePart.id}?unit_id=${activeUnitId}`"
        >
          <v-icon class="mr-half">mdi-play</v-icon>
          Плейлист
        </v-button>

        <div class="d-flex header__victories mr-3">
          <span class="text-h3">{{ student.victories_count }}</span>
          <v-icon size="32" class="ml-1" :class="student.victories_count ? 'orange-text' : 'gray-text'">
            mdi-star
          </v-icon>
        </div>

        <!-- Tails menu -->
        <v-menu-base pos-horizontal="right">
          <template #activator="{ onclick }">
            <div class="d-flex header__tails mr-3 align-center" @click="onclick">
              <span class="text-h3">{{ student.tails.length }}</span>
              <v-icon v-if="student.tails.length" size="28" class="ml-1"> home-smile-red </v-icon>
              <v-icon v-else size="28" class="ml-1"> home-smile-green </v-icon>
            </div>
          </template>

          <div v-if="student.tails.length" class="student-tails-menu pa-3">
            <h2 class="text-title-bold">
              Осталось <span class="red-text">{{ tailsStr }}!</span>
            </h2>

            <p class="mt-1">Это твои несданные уроки.</p>
            <p class="mt-1">Вернись к этим заданиям и помоги вернуть хвосты хозяевам.</p>

            <div v-for="(tail, i) in student.tails" :key="i" class="d-flex mt-2 align-center">
              <div
                class="student-tails-menu__tail-icon"
                :style="{ backgroundImage: `url('${asset(`img/animals/animal-tail-${getAnimalNum(tail)}.png`)}')` }"
              ></div>

              <div class="ml-2">
                {{ tail.name }}<br />
                <span class="text-caption gray-text">{{ tail.junit_name }}</span>
              </div>
            </div>
          </div>

          <div v-else class="student-tails-menu pa-3">
            <h2 class="text-title-bold">Молодец, хвостов нет!</h2>

            <div class="student-tails-menu__placeholder mt-2">
              <img :src="asset(`img/animals/anim/animal-happy-${randomAnimalNum}.png`)" />
            </div>
          </div>
        </v-menu-base>

        <div class="d-flex header__crystals" @click="noCrystalsModal = true">
          <v-icon size="32">crystal</v-icon>
          <v-icon class="header__crystals-question blue-text" size="20">mdi-help-circle</v-icon>
        </div>
      </div>

      <div class="header-v2">
        <div class="info">
          <div class="title">{{ coursePart.title }}</div>
          <div class="part">{{ coursePart.part }}</div>
        </div>
        <div class="aside">
          <a :href="`/student/choose_course/${student.id}`" class="switch"></a>
        </div>
      </div>

      <div v-if="student.id === 0" class="demo-cabinet-card text-body-bold mt-3">
        <v-icon class="orange-text mr-2">mdi-information</v-icon>
        Это демо-версия кабинета ученика. Прослушивания не будут учтены в статистике.
      </div>

      <div class="switch-unit mt-2">
        <v-select :model-value="activeUnitId" :items="unitSelectItems" @update:model-value="onUnitSelect" />
      </div>

      <div v-if="!activeUnit?.unlocked && activeUnit?.lock_reason === 'need_login'" class="locked-unit">
        <v-unauthorized-card level="user" title="Юнит недоступен">
          В демо-кабинете ученика доступен только первый юнит. Для доступа к другим юнитам войдите на сайт МирГоворит
          или зарегистрируйтесь.
        </v-unauthorized-card>
      </div>

      <div v-else class="materials">
        <div class="list">
          <template v-for="material in uniqueMaterials" :key="material.id">
            <h3 v-if="materials.length > 1" class="materials__title mb-1">{{ material.name }}</h3>
            <v-audio-player
              class="mb-3"
              :material="material"
              :data-material-id="material.id"
              :file-prefix="getMaterialFilePrefix()"
              :student-id="student.id"
              @play="onAudioPlay(material.id)"
            />
          </template>
        </div>
      </div>

      <section v-if="smartBook" class="my-4">
        <h3 class="d-flex align-center text-h3">
          <v-icon class="mr-1">lamp</v-icon>
          Умный учебник
        </h3>

        <p class="mt-1">
          Выполняй задания и&nbsp;зарабатывай бонусы. Накопленные бонусы можно потратить в кабинете аватара.
        </p>

        <StudentSmartBookCard
          class="mt-4"
          :unit-name="activeUnit?.title ?? ''"
          :student-id="student.id"
          :book="smartBook"
          :has-tails="student.tails.length > 0"
          :demo-url="smartbookDemoUrl"
        />
      </section>

      <template v-if="games && games.length">
        <template v-if="activeUnitIndex !== 0 && purchasedGames.length">
          <h3 class="d-flex games__title mb-1 mb-d-2 mt-4 mt-d-3">Приложения</h3>

          <p class="games__description mb-2">Купленные приложения будут доступны в течение года.</p>

          <div class="games mt-2 mb-6">
            <GameCard
              v-for="game in purchasedGames"
              :key="game.id"
              :friendly_name="game.friendly_name"
              :cover_image="game.cover_image"
              :unlocked="game.unlocked"
              :lock_reason="game.lock_reason"
              :draft="game.draft"
              :demo="game.demo"
              @click="openGame(game)"
              @show-help="() => gameHelpModal?.$open(game.name)"
            />
          </div>
        </template>

        <template v-if="activeUnitIndex === 0 || notPurchasedGames.length">
          <h3 class="d-flex games__title mb-1 mb-d-2 mt-4 mt-d-3">
            <template v-if="activeUnitIndex === 0">Приложения</template>
            <template v-else>Магазин приложений</template>
          </h3>

          <p v-if="activeUnitIndex > 0" class="games__description mb-2">
            Купленные приложения будут доступны в течение года.
          </p>
        </template>

        <template v-if="activeUnitIndex === 0">
          <p v-if="coursePart.first" class="games__description mb-2">
            В первом юните можно попробовать бесплатно любое приложение - тебе будет доступен только один уровень.
          </p>
          <p v-else class="games__description mb-2">
            В первой части курса можно попробовать бесплатно любое приложение - тебе будет доступен только один уровень.
          </p>

          <div class="games mt-2">
            <GameCard
              v-for="game in games"
              :key="game.id"
              :friendly_name="game.friendly_name"
              :cover_image="game.cover_image"
              :price="game.price"
              :unlocked="game.unlocked"
              :lock_reason="game.lock_reason"
              :draft="game.draft"
              :demo="game.demo"
              @click="openGame(game)"
              @show-help="() => gameHelpModal?.$open(game.name)"
            />
          </div>
        </template>

        <div v-else class="games mt-2">
          <GameCard
            v-for="game in notPurchasedGames"
            :key="game.id"
            :friendly_name="game.friendly_name"
            :cover_image="game.cover_image"
            :price="game.price"
            :unlocked="game.unlocked"
            :lock_reason="game.lock_reason"
            :draft="game.draft"
            :demo="game.demo"
            @click="openGame(game)"
            @show-help="() => gameHelpModal?.$open(game.name)"
          />
        </div>
      </template>
    </div>

    <v-modal
      :model-value="!!activeGame"
      :mode="isActiveGameIntegrated ? 'dark' : ''"
      class="game-modal"
      title="Редактировать фото"
      @update:model-value="activeGame = null"
    >
      <template #header>
        <div class="game-modal__header">
          <h3 v-if="!isActiveGameIntegrated" class="game-modal__title text-h3">
            {{ activeGame?.friendly_name }}
          </h3>
        </div>
      </template>
      <div v-if="activeGame" class="game-modal__content">
        <template v-if="isActiveGameIntegrated">
          <GameKaraoke
            v-if="activeGame.name === 'karaoke'"
            :game="activeGame"
            :unit-id="activeUnitId"
            :student-id="student.id"
            :course-name="coursePart.title"
            @play="canPingGameActivity = true"
            @pause="canPingGameActivity = false"
          />
          <GameShadowing
            v-if="
              activeGame.name === 'shadowing' ||
              activeGame.name === 'videoteacher' ||
              activeGame.name === 'irregularverbs'
            "
            :game="activeGame"
            :unit-id="activeUnitId"
            :student-id="student.id"
            @play="canPingGameActivity = true"
            @pause="canPingGameActivity = false"
          />
        </template>
        <iframe v-else :src="`${activeGame.url}&frame=1`" frameborder="0" width="100%" height="100%"></iframe>
      </div>
    </v-modal>

    <v-rich-modal v-model="noCrystalsModal" title="Игровой счёт">
      <div style="max-width: 700px">
        <StudentCrystalsNotificationCard class="mb-2" />
        <v-button mode="primary filled block large" @click="openShop"> Открыть магазин </v-button>
      </div>
    </v-rich-modal>

    <StudentGamePurchaseModal
      v-model="orderGame"
      :active-unit-id="activeUnitId"
      :units="coursePart.units"
      :course-part="coursePart"
      @confirm="confirmPurchase"
    />
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/student/view_unit.scss' as *;
</style>
